import cross from "@/images/cross.svg";
import { Image } from "@/ui/image";
import { KeywordsHandler } from "@/ui/keywords-handler";
import { DialogDismiss, DialogStore, Form, FormStore } from "@ariakit/react";
import { ReactNode } from "react";
import { FormSubmitButton } from "../form-submit-button";
import { FormSubmitError } from "../form-submit-error";
import { ModalDialog } from "../modal-dialog";
import { primaryTheme } from "../utils.css";
import * as css from "./style.css";

type DialogHeaderProps = {
  heading: string;
  subheading?: string;
};

export function DialogHeader(props: DialogHeaderProps) {
  return (
    <div className={css.header}>
      <div />
      <div>
        <h1 className={css.heading}>{props.heading}</h1>

        {props.subheading && <div className={css.subheading}>{props.subheading}</div>}
      </div>

      <DialogDismiss>
        <Image src={cross} alt="Dismiss" width={40} height={40} />
      </DialogDismiss>
    </div>
  );
}

type FormDialogProps = {
  store: DialogStore;
  children: ReactNode;
  onSubmit?: () => Promise<void>;
  submit: string;
  disabled?: boolean;
};

/**
 * @deprecated Use {@link FormDialogV2} where possible.
 */
export function FormDialog(props: FormDialogProps & DialogHeaderProps) {
  return (
    <ModalDialog store={props.store} className={css.dialog}>
      <KeywordsHandler>
        <form
          className={css.dialogContent}
          onSubmit={async (event) => {
            event.preventDefault();

            if (!props.disabled) {
              try {
                await props.onSubmit?.();
                props.store.hide();
              } catch (error) {
                console.error(error);
              }
            }
          }}
        >
          <DialogHeader {...props} />

          {props.children}

          <div className={css.submitButtonContainer}>
            <button
              type="submit"
              className={css.submitButton}
              style={primaryTheme}
              disabled={props.disabled}
            >
              {props.submit}
            </button>
          </div>
        </form>
      </KeywordsHandler>
    </ModalDialog>
  );
}

type FormDialogV2Props = {
  form: FormStore;
  children: ReactNode;
  submitLabel: NonNullable<ReactNode>;
  formSubmitName: string;
};

/**
 * Renders a modal dialog with nested form.
 *
 * Note that compared to {@link FormDialog} (v1), this version relies on AriaKit forms.
 */
export function FormDialogV2(props: FormDialogV2Props & DialogHeaderProps) {
  return (
    <ModalDialog className={css.dialog}>
      <KeywordsHandler>
        <Form
          store={props.form}
          autoComplete="off"
          className={css.dialogContent}
          resetOnSubmit={false}
        >
          <DialogHeader {...props} />

          {props.children}

          <div className={css.submitButtonContainer}>
            {props.formSubmitName && <FormSubmitError name={props.formSubmitName} />}

            {typeof props.submitLabel === "string" ?
              <FormSubmitButton label={props.submitLabel} className={css.submitButton} />
            : props.submitLabel}
          </div>
        </Form>
      </KeywordsHandler>
    </ModalDialog>
  );
}
